import { Link } from 'react-router-dom'
import { Receipt, Users, BriefcaseMedical, CalendarRange, Tag, ChevronRight } from 'lucide-react'
import { useMe } from '../lib/me.js'
import { canAccessStaff, canViewPromotion } from '../lib/role.js'

// Manager landing — a grid of manager-only tools. More tools (payroll, stock
// reports, …) slot in as sibling cards. (สินทรัพย์ moved to the Stock submenu.)
// A tool may carry a `gate(me)` predicate when its target has a stricter access
// rule than the Manager area itself.
const TOOLS = [
  {
    to: '/manager/expense',
    icon: Receipt,
    title: 'ค่าใช้จ่าย (Expense)',
    desc: 'บันทึกบัญชีค่าใช้จ่าย รายการซื้อ VAT / หัก ณ ที่จ่าย และบริษัทผู้ขาย',
  },
  {
    // /manager/employee is gated by RequireStaff (Manager/HR/Admin, Stock Manager
    // OUT) — hide the card from anyone who couldn't actually enter it.
    to: '/manager/employee',
    icon: Users,
    title: 'พนักงาน (Employee)',
    desc: 'ข้อมูลพนักงาน การลงเวลา การลา รายการหักประจำ และเงินเดือน',
    gate: canAccessStaff,
  },
  {
    // Same RequireStaff gate as พนักงาน — hide it from anyone who can't enter.
    to: '/manager/schedule',
    icon: CalendarRange,
    title: 'ตารางเวร (Schedule)',
    desc: 'จัดตารางออกตรวจของทันตแพทย์ และตารางเวร/กะของพนักงาน รายเดือน',
    gate: canAccessStaff,
  },
  {
    to: '/manager/dentist',
    icon: BriefcaseMedical,
    title: 'ทันตแพทย์ (Dentist)',
    desc: 'รายชื่อหมอ ความเชี่ยวชาญ ใบประกอบวิชาชีพ สถานะการทำงาน รายได้รายเดือน และประกาศถึงทันตแพทย์',
  },
  {
    // Narrower than the Manager area itself — Stock Manager is out (RequirePromotion),
    // so hide the card from anyone who couldn't actually enter it.
    to: '/manager/promotion',
    icon: Tag,
    title: 'โปรโมชั่น (Promotion)',
    desc: 'รายการโปรโมชั่น ราคา ค่ามือหมอ คอมมิชชั่น และรูปแบบการเก็บเงิน (pattern)',
    gate: canViewPromotion,
  },
]

export default function Manager() {
  const me = useMe()
  const tools = TOOLS.filter((t) => !t.gate || t.gate(me))
  return (
    <div className="px-10 py-8 max-w-[1100px]">
      <h1 className="text-[22px] font-semibold text-slate-900 tracking-tight mb-1">Manager</h1>
      <p className="text-[14px] text-slate-500 mb-7">เครื่องมือสำหรับผู้จัดการ</p>

      <div className="grid gap-4 sm:grid-cols-2">
        {tools.map(({ to, icon: Icon, title, desc }) => (
          <Link
            key={to}
            to={to}
            className="group rounded-2xl border border-slate-200 bg-white p-5 shadow-sm hover:border-brand-300 hover:shadow-md transition flex items-start gap-4"
          >
            <div className="w-11 h-11 rounded-xl bg-brand-50 text-brand-600 flex items-center justify-center shrink-0">
              <Icon size={22} strokeWidth={1.75} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1 text-[15.5px] font-semibold text-slate-900">
                {title}
                <ChevronRight size={16} className="text-slate-300 group-hover:text-brand-400 transition" />
              </div>
              <p className="text-[13px] text-slate-500 mt-1 leading-relaxed">{desc}</p>
            </div>
          </Link>
        ))}
      </div>
    </div>
  )
}
